import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Info, XCircle } from 'phosphor-react';
import { UserLoginAttempt } from '@dill/dill-shared';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierUserLoginAttempts,
  rejectUserLoginAttempt,
} from '../../../../redux/services/userLoginAttemptsService';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillTable, DillColumn, DillRowAction } from '../../../general/Dill/DillTable';
import { DillButton } from '../../../general/Dill/DillButton';

/**
 * UserLoginAttemptsModal - The supplier's queue of people who tried to log in but have no access.
 *
 * A row leaves this list one of two ways: the supplier rejects it here, or the person is invited
 * through Customers > Account Access > Invite Users, which resolves the attempt automatically.
 * There is deliberately no "approve" button — approving means granting real access, and that
 * happens in the customer record where the right buyer account and permissions can be chosen.
 */
export const UserLoginAttemptsModal = ({
  open,
  onClose,
  supplierId,
}: {
  open: boolean;
  onClose: () => void;
  supplierId: string;
}) => {
  const dispatch = useAppDispatch();
  const { userLoginAttempts, loadingList } = useAppSelector((state) => state.userLoginAttempts);
  const [attemptToReject, setAttemptToReject] = useState<UserLoginAttempt | null>(null);

  const isLoading = loadingList.includes('getSupplierUserLoginAttempts');
  const isRejecting = loadingList.includes('rejectUserLoginAttempt');

  useEffect(() => {
    if (open && supplierId) {
      dispatch(getSupplierUserLoginAttempts({ supplierId }));
    }
  }, [open, supplierId, dispatch]);

  // These add up to 778, which is the full width available inside the modal: 900 less
  // DillModal's px-5 padding (40), the row-actions column (80) and the table's own ~2px border.
  // Leaving them short hands the leftover to the last cell, which bloats the action gutter and
  // squeezes Email. Each cell also spends 32px on its px-4 padding, so usable text is width-32.
  const columns = useMemo<DillColumn<UserLoginAttempt>[]>(
    () => [
      {
        key: 'email',
        label: 'Email',
        // 308px of text fits ~47 characters, so ordinary addresses stay on one line instead of
        // wrapping a stray character onto a second row
        width: 340,
        sortable: true,
        getSearchValue: (row) => row.email,
        // break-all is the fallback for genuinely long addresses: wrap rather than widen the table
        render: (_value, row) => (
          <span className="font-medium text-gray-900 break-all leading-tight">{row.email}</span>
        ),
      },
      {
        key: 'firstAttemptedAt',
        label: 'Requested',
        width: 140,
        sortable: true,
        getSortValue: (row) => moment(row.firstAttemptedAt).valueOf(),
        render: (_value, row) => (
          <div className="flex flex-col">
            <span>{moment(row.firstAttemptedAt).format('MMM D, YYYY')}</span>
            <span className="text-2xs text-gray-400">{moment(row.firstAttemptedAt).fromNow()}</span>
          </div>
        ),
      },
      {
        key: 'customerAccountName',
        label: 'Company',
        width: 202,
        sortable: true,
        getSearchValue: (row) => row.customerAccountName ?? '',
        render: (_value, row) =>
          row.customerAccountName ? (
            <span>{row.customerAccountName}</span>
          ) : (
            <span className="text-gray-400">Not provided</span>
          ),
      },
      {
        key: 'attemptCount',
        label: 'Tries',
        // 64px of text: enough for the header plus its sort icon without wrapping
        width: 96,
        numeric: true,
        sortable: true,
        getSortValue: (row) => row.attemptCount ?? 1,
        // Repeat attempts are the signal worth noticing, so a single try stays visually quiet
        render: (_value, row) => {
          const attempts = row.attemptCount ?? 1;
          return attempts > 1 ? (
            <span className="font-semibold text-gray-900">{attempts}</span>
          ) : (
            <span className="text-gray-400">1</span>
          );
        },
      },
    ],
    []
  );

  const rowActions = useMemo<DillRowAction<UserLoginAttempt>[]>(
    () => [
      {
        key: 'reject',
        label: 'Reject',
        variant: 'danger',
        icon: <XCircle size={18} />,
        onClick: (row) => setAttemptToReject(row),
      },
    ],
    []
  );

  const handleReject = async () => {
    if (!attemptToReject) {
      return;
    }
    await dispatch(rejectUserLoginAttempt({ supplierId, userLoginAttemptId: attemptToReject.id }));
    setAttemptToReject(null);
  };

  return (
    <>
      <DillModal
        open={open}
        onClose={onClose}
        title="User login attempts"
        subtitle="People who tried to sign in to your portal but do not have access yet"
        maxWidth="900px"
        closeOnOverlayClick={true}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 p-3 rounded-lg bg-blue-50 text-xs text-gray-700">
            <div className="shrink-0 pt-0.5">
              <Info size={16} />
            </div>
            <p>
              To accept this user login attempt and provide the right access to the user, you will
              need to go into the &quot;Customers&quot; tab and click &quot;Account Access&quot;
              then &quot;Invite Users.&quot; An invitation email will then be sent to the customer
              email to join the online AR portal.
            </p>
          </div>

          <DillTable<UserLoginAttempt>
            columns={columns}
            data={userLoginAttempts}
            rowKey={(row) => row.id}
            search={true}
            searchPlaceholder="Search email or company..."
            striped={true}
            loading={isLoading}
            emptyMessage="No pending login attempts. Anyone who requests access will show up here."
            defaultSort={{ key: 'firstAttemptedAt', dir: 'asc' }}
            rowActions={rowActions}
            rowActionsMode="always"
            stickyRowActions
            // No scrollX: it makes DillTable scale the columns to fill containerWidth
            // (DillTable.tsx:1082) without reserving room for the row-actions column, so the
            // table always ends up 80px wider than its container and the last column hides
            // under the sticky action cell. Native table layout counts every cell, so the
            // widths below fit and nothing scrolls sideways.
            maxHeight="55vh"
            countLabel="login attempts"
          />
        </div>
      </DillModal>

      <DillModal
        open={!!attemptToReject}
        onClose={() => setAttemptToReject(null)}
        title="Reject this login attempt?"
        subtitle={attemptToReject?.email}
        maxWidth="440px"
        overlayZIndex={60}
        footer={
          <div className="flex justify-end gap-2">
            <DillButton
              label="Cancel"
              variant="secondary"
              onClick={() => setAttemptToReject(null)}
            />
            <DillButton
              label="Reject request"
              variant="danger"
              isLoading={isRejecting}
              onClick={handleReject}
            />
          </div>
        }>
        <p className="text-xs text-gray-600">
          This removes the request from your list and stops Dill following up on it. If they need
          access later, they can request it again or you can invite them from the Customers tab.
        </p>
      </DillModal>
    </>
  );
};
